import { useContext, memo, useState, useRef, useMemo } from 'react';

import { Input, Form } from 'antd';
import { styled } from 'styled-components';

import { LayoutContext } from '@/layouts/baseLayout/provider';
import useUserStore from '@/store/user';
import { _globalEmptyResult } from '@/utils/_globalBuriedPoint';
import { buriedPoint } from '@/utils/buriedPoint';
import { _loadQQ } from '@/utils/common';
import Utils from '@/utils/Utils';

const CustomDiv = styled.div`
  .ant-form {
    display: flex;
    padding-bottom: 28px;
  }
  .ant-form-item {
    margin-top: 25px;
    .ant-form-item-explain {
      transform: translateY(5px);
      font-size: 12px;
    }
  }
  .input-form-item {
    width: 254px;
    height: 40px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    margin-right: 20px;
    display: flex;
    align-items: center;
    padding: 0 11px;
  }
  .num-form-item {
    width: 174px;
  }
  .btn-form-item {
    width: 102px;
    height: 38px;
    display: flex;
    align-items: center;
  }
  .ant-input {
    padding: 4px 0;
  }
  .ant-input::placeholder {
    font-size: 14px;
    color: #bfbfbf;
  }
`;

const EmptyResult = (props) => {
  const { globalSearchKeyword } = props;
  const { resourceData } = useContext(LayoutContext);
  const { commonConfigVO } = useUserStore();
  const [form] = Form.useForm();
  const [resetProductModel, setResetProductModel] = useState(false);

  const safeSearchKeyword = useMemo(() => {
    return Utils.getSafeSearchKeyword(globalSearchKeyword);
  }, [globalSearchKeyword]);

  const zlBtnRef = useRef(null);

  const handleFinish = (valuse) => {
    buriedPoint({
      op: _globalEmptyResult['go_find_page'],
      ty: 'click',
      data: {
        kw: globalSearchKeyword,
        track_zone: props?.track_zone,
        find: productModel || globalSearchKeyword,
      },
    });
    let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/stock/and/find.html`;
    const { productModel, brandName, expectNumber, expectPrice } = valuse;
    const params = [];
    if (productModel) {
      params.push(`productModel=${encodeURI(encodeURI(productModel))}`);
    }
    if (brandName) {
      params.push(`brandName=${encodeURI(encodeURI(brandName))}`);
    }
    if (expectNumber) {
      params.push(`expectNumber=${encodeURI(encodeURI(expectNumber))}`);
    }
    if (expectPrice) {
      params.push(`expectPrice=${encodeURI(encodeURI(expectPrice))}`);
    }
    if (params.join('&')) {
      url = url + '?' + params.join('&') + '&newInit=true';
    }
    window.open(Utils.getBtnSpmQueryUrl(url, zlBtnRef?.current), '_blank');
    if (resetProductModel) {
      form.setFieldsValue({ productModel: '' });
    }
  };

  const handleSubmit = async () => {
    const values = await form.getFieldsValue();
    if (!values.productModel && safeSearchKeyword) {
      form.setFieldsValue({ productModel: safeSearchKeyword });
      setResetProductModel(true);
    } else {
      setResetProductModel(false);
    }
    form.submit();
  };

  return (
    <section className={'w-full'}>
      <div className="my-[71px] flex justify-center">
        <div className="mr-[28px] h-[82px] w-[109px]">
          <img className="w-full" src="https://static.szlcsc.com/ecp/assets/newWeb/so/empty-2.svg" alt="没有找到商品" />
        </div>
        <div className="text-[#444] max-w-[800px]">
          <p className="mb-[10px]  flex flex-wrap break-all text-[16px]  font-medium">
            很抱歉，未找到相匹配的商品，建议您
          </p>
          <p className="mb-[6px]  text-[14px]">1、检查输入的搜索词是否正确，或者尝试使用不同的关键字重新搜索</p>
          <p className="text-[14px] ">
            2、填写下方找料需求为您找料，或者联系
            <span
              className={'cursor-pointer text-[#0093E6] underline'}
              onClick={() => {
                _loadQQ(commonConfigVO?.salesmanVO?.qq || '4000800709');
                buriedPoint({
                  op: _globalEmptyResult['connect_service'],
                  ty: 'click',
                  data: {
                    kw: globalSearchKeyword,
                  },
                });
              }}
            >
              在线客服
            </span>
            进行找料
          </p>
        </div>
      </div>
      <div className={'mx-auto mb-[71px] w-[1200px] rounded-[10px] bg-white px-[20px] py-[17px]'}>
        <div className={'mb-[10px] text-[18px] font-bold text-[#111111]'}>
          快速<span className={'mb-[12px] text-[#0093E6]'}>找料</span>
        </div>
        <CustomDiv className={'w-full'}>
          <Form form={form} autoComplete="off" preserve={false} onFinish={handleFinish} layout={'horizontal'}>
            <div className={'input-form-item'}>
              <Form.Item
                label="型号"
                name="productModel"
                colon={false}
                rules={[
                  { required: true, message: '请输入商品型号' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      const { zhCN, phone, email, idCard, bankCard } = Utils.getCommonRegExp();

                      // 检查是否包含中文
                      if (zhCN.test(value)) {
                        return Promise.reject('不能包含中文字符');
                      }

                      // 检查电话号码
                      if (phone.test(value)) {
                        return Promise.reject('不能为电话号码');
                      }

                      // 检查邮箱
                      if (email.test(value)) {
                        return Promise.reject('不能为邮箱地址');
                      }

                      // 检查身份证号（15位或18位）
                      if (idCard.test(value)) {
                        return Promise.reject('不能为身份证号码');
                      }

                      // 检查银行卡号（13-19位数字）
                      if (bankCard.test(value)) {
                        return Promise.reject('不能为银行卡号码');
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input bordered={false} placeholder={safeSearchKeyword || '请输入型号'} maxLength={100} />
              </Form.Item>
            </div>
            <div className={'input-form-item'}>
              <Form.Item label="品牌" name="brandName" colon={false}>
                <Input bordered={false} placeholder={'请输入品牌'} maxLength={32} />
              </Form.Item>
            </div>
            <div className={'input-form-item num-form-item'}>
              <Form.Item
                label="数量"
                name="expectNumber"
                colon={false}
                rules={[
                  {
                    required: false,
                  },
                  {
                    pattern: /^[1-9]\d*$/,
                    message: '请输入正确数量',
                  },
                ]}
              >
                <Input bordered={false} placeholder={'请输入数量'} maxLength={8} />
              </Form.Item>
            </div>
            <div className={'input-form-item'}>
              <Form.Item
                label="期望单价(元)"
                name="expectPrice"
                colon={false}
                rules={[
                  {
                    required: false,
                  },
                  {
                    validator: (_, value) => {
                      if ((value && isNaN(value)) || parseFloat(value) <= 0) {
                        return Promise.reject('请输入正确价格');
                      } else if (value && Number(value.split('.')[0]) > 1000000) {
                        return Promise.reject('整数最大支持1000000');
                      } else if (value && value.includes('.') && value.split('.')[1].length > 6) {
                        return Promise.reject('小数最多支持6位');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input bordered={false} placeholder={'请输入单价'} maxLength={14} />
              </Form.Item>
            </div>
            <div className={'btn-form-item'}>
              <Form.Item label="" colon={false}>
                <button
                  onClick={handleSubmit}
                  ref={zlBtnRef}
                  className={
                    'bg-[#0093E6] text-[#FFFFFF] rounded-[6px] h-[40px] w-[102px] flex items-center hover:opacity-80 justify-center'
                  }
                >
                  我要找料
                </button>
              </Form.Item>
            </div>
          </Form>
        </CustomDiv>
      </div>
    </section>
  );
};

export default memo(EmptyResult);
